import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from '@clofo/i18n'
import { clofoApi } from '@clofo/data/api'
import { Alert, AlertDescription, AlertTitle } from '@clofo/ui/alert'
import { Button } from '@clofo/ui/button'
import { IceIcon } from '@clofo/ui/ice-icon'
import { SubmitButton } from '@clofo/ui/submit-button'
import { PasswordFields, passwordFieldsShape } from './password-fields'

export const ResetPasswordForm = ({ uidb64, token }: { uidb64: string; token: string }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSubmitError, setShowSubmitError] = useState(false)

  const schema = z.object(passwordFieldsShape(t)).refine(v => v.newPassword1 === v.newPassword2, {
    error: t('login.passwordFields.mismatch'),
    path: ['newPassword2'],
  })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword1: '', newPassword2: '' },
  })

  const onSubmit = async ({ newPassword1, newPassword2 }: Values) => {
    setShowSubmitError(false)
    try {
      await clofoApi.auth.completeResetPassword({
        uidb64,
        token,
        new_password1: newPassword1,
        new_password2: newPassword2,
      })
      navigate('/', { replace: true })
    } catch {
      // Token consumed or invalidated between page load and submit — stay on the page, matching
      // the legacy behaviour (inline alert, not a redirect to token-error).
      setShowSubmitError(true)
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col w-full'>
      {showSubmitError && (
        <Alert variant='error' className='mb-6'>
          <AlertTitle className='flex items-center justify-between'>
            {t('login.reset.errorAlertTitle')}
            <Button
              type='button'
              variant='icon'
              aria-label={t('login.reset.dismissError')}
              onClick={() => setShowSubmitError(false)}
            >
              <IceIcon name='close-delete-x' />
            </Button>
          </AlertTitle>
          <AlertDescription>
            <p>
              <Trans
                t={t}
                i18nKey='login.reset.errorAlertDescription'
                components={{ a: <a className='underline' /> }}
              />
            </p>
          </AlertDescription>
        </Alert>
      )}

      <p className='mb-8 leading-relaxed'>{t('login.reset.completeForm')}</p>

      <PasswordFields control={form.control} />

      <hr className='my-6 border-secondary' />

      <div className='flex gap-3'>
        <Button type='button' variant='secondary' size='lg' onClick={() => navigate('/')}>
          {t('login.reset.cancel')}
        </Button>
        <SubmitButton type='submit' size='lg' isSubmitting={form.formState.isSubmitting}>
          {t('login.reset.save')}
        </SubmitButton>
      </div>
    </form>
  )
}
